import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { CoachReviewRecord, Requirement, DraftSection, FormatRule, RequiredForm } from "../types";

/**
 * Subscribes to the latest coach review record for an opportunity
 */
export function subscribeToLatestCoachReview(
  opportunityId: string,
  callback: (review: CoachReviewRecord | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "coach_reviews");
  const q = query(colRef, orderBy("generatedAt", "desc"), limit(1));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty || !snapshot.docs[0]) {
        callback(null);
        return;
      }

      const docSnap = snapshot.docs[0];
      const data = docSnap.data();

      const record: CoachReviewRecord = {
        id: docSnap.id,
        opportunityId,
        overallScore: typeof data.overallScore === "number" ? data.overallScore : 0,
        summary: data.summary || "",
        findings: Array.isArray(data.findings) ? data.findings : [],
        passedChecksCount: typeof data.passedChecksCount === "number" ? data.passedChecksCount : 0,
        totalChecksCount: typeof data.totalChecksCount === "number" ? data.totalChecksCount : 0,
        modelUsed: data.modelUsed || "",
        generatedAt: data.generatedAt || new Date().toISOString(),
        generatedBy: data.generatedBy || "",
        generatedByEmail: data.generatedByEmail || "",
      };

      callback(record);
    },
    (err) => {
      console.error(`Error subscribing to coach reviews for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Invokes the phq_coachReview Cloud Function to audit drafts against RFP criteria
 */
export async function runCoachReview(params: {
  opportunityId: string;
  requirements: Requirement[];
  draftSections: DraftSection[];
  formatRules: FormatRule[];
  requiredForms: RequiredForm[];
}): Promise<CoachReviewRecord> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be logged in to run the Proposal Writing Coach audit.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_coachReview`;

  const payload = {
    opportunityId: params.opportunityId,
    requirements: params.requirements.map((r) => ({
      id: r.id,
      title: r.title,
      prompt: r.prompt,
      mandatory: Boolean(r.mandatory),
      category: r.category,
    })),
    draftSections: params.draftSections.map((s) => ({
      id: s.id,
      requirementId: s.requirementId,
      title: s.title,
      content: s.content,
      status: s.status,
    })),
    formatRules: params.formatRules.map((f) => ({
      id: f.id,
      ruleType: f.ruleType,
      requirement: f.requirement,
      isCompliant: Boolean(f.isCompliant),
    })),
    requiredForms: params.requiredForms.map((rf) => ({
      id: rf.id,
      formName: rf.formName,
      mandatory: Boolean(rf.mandatory),
      status: rf.status,
    })),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Coach audit failed with status ${response.status}`);
  }

  const resData = await response.json();
  return {
    id: resData.reviewId,
    opportunityId: params.opportunityId,
    overallScore: resData.overallScore,
    summary: resData.summary,
    findings: resData.findings || [],
    passedChecksCount: resData.passedChecksCount || 0,
    totalChecksCount: resData.totalChecksCount || 0,
    modelUsed: resData.modelUsed || "gemini-3.8-flash",
    generatedAt: resData.generatedAt || new Date().toISOString(),
    generatedBy: currentUser.displayName || currentUser.email || "User",
    generatedByEmail: currentUser.email || "",
  };
}
